import { useEffect, type ComponentType } from 'react';
import { Card, Divider, HStack, Heading, Link, Text, VStack } from '@astryxdesign/core';
import { Link as RouterLink, useParams } from 'react-router';

import { NotFoundPage } from './NotFound';
import { TimesfmVsProphetArticle } from './blog/TimesfmVsProphet';
import { QuantileForecastingExplainedArticle } from './blog/QuantileForecastingExplained';
import { BatchForecastingApiArticle } from './blog/BatchForecastingApi';
import { ForecastApiRateLimitsArticle } from './blog/ForecastApiRateLimits';
import { SeasonalNaiveBaselineArticle } from './blog/SeasonalNaiveBaseline';
import {
  adjacentPosts,
  findPost,
  formatPostDate,
  postPath,
  postShareImage,
  relatedPosts,
  type BlogPost,
} from '../blog/posts';
import { authorInitials, findAuthor, type Author } from '../blog/authors';
import { JsonLd } from '../seo/JsonLd';
import { SITE_URL } from '../seo/site';
import { track } from '../analytics/track';

/**
 * Slug → body. The one place articles need React, which is why
 * `src/blog/posts.ts` stays JSX-free and importable from the build scripts.
 */
const ARTICLES: Record<string, ComponentType> = {
  'timesfm-vs-prophet-benchmark': TimesfmVsProphetArticle,
  'quantile-forecasting-explained': QuantileForecastingExplainedArticle,
  'batch-time-series-forecasting-api': BatchForecastingApiArticle,
  'forecast-api-rate-limits': ForecastApiRateLimitsArticle,
  'seasonal-naive-baseline': SeasonalNaiveBaselineArticle,
};

/**
 * schema.org BlogPosting.
 *
 * `author` is a Person and `publisher` is the company, which is the split
 * search engines actually model: expertise belongs to people, and a benchmark
 * that publishes its own losses is only as credible as the name on it. The
 * author's `url` is emitted only when there is a real page to point at.
 */
function articleSchema(post: BlogPost, author: Author | undefined) {
  const url = `${SITE_URL}${postPath(post.slug)}`;
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.headline,
    description: post.description,
    datePublished: post.published,
    dateModified: post.updated ?? post.published,
    inLanguage: 'en',
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    url,
    image: `${SITE_URL}${postShareImage(post)}`,
    author: author
      ? { '@type': 'Person', name: author.name, ...(author.url ? { url: author.url } : {}) }
      : { '@type': 'Organization', name: 'VecTime Cloud', url: SITE_URL },
    publisher: { '@type': 'Organization', name: 'VecTime Cloud', url: SITE_URL },
  };
}

/** Portrait if there is one, initials if there is not. Never a broken image. */
function AuthorMark({ author, size }: { author: Author; size: 'sm' | 'lg' }) {
  if (author.avatar) {
    return <img className={`author-mark author-mark--${size}`} src={author.avatar} alt="" width={size === 'lg' ? 48 : 28} height={size === 'lg' ? 48 : 28} />;
  }
  return (
    <span className={`author-mark author-mark--${size} author-mark--initials`} aria-hidden="true">
      {authorInitials(author.name)}
    </span>
  );
}

/** Name, role and date, directly under the headline. */
function Byline({ author, post }: { author: Author | undefined; post: BlogPost }) {
  return (
    <HStack gap={1.5} vAlign="center" wrap="wrap">
      {author ? <AuthorMark author={author} size="sm" /> : null}
      <VStack gap={0}>
        {author ? (
          <Text size="sm">
            {author.url ? <Link href={author.url}>{author.name}</Link> : author.name}
            <Text as="span" size="sm" color="secondary">
              {` · ${author.role}`}
            </Text>
          </Text>
        ) : null}
        <Text size="sm" color="secondary">
          <time dateTime={post.published}>{formatPostDate(post.published)}</time>
          {` · ${post.readingMinutes} min read`}
          {post.updated ? (
            <>
              {' · updated '}
              <time dateTime={post.updated}>{formatPostDate(post.updated)}</time>
            </>
          ) : null}
        </Text>
      </VStack>
    </HStack>
  );
}

/**
 * In-page contents.
 *
 * Sections come from `posts.ts` rather than from walking the DOM, so the list
 * is present in the prerendered HTML — a contents block that only appears once
 * React has hydrated is no use to the reader who arrived from a search and is
 * deciding in two seconds whether the page answers their question.
 */
function Contents({ post }: { post: BlogPost }) {
  if (post.sections.length < 3) return null;
  return (
    <nav className="toc" aria-label="On this page">
      <Text size="sm" color="secondary" className="toc__label">
        On this page
      </Text>
      <ol className="toc__list">
        {post.sections.map((section) => (
          <li key={section.id}>
            <a className="toc__link" href={`#${section.id}`}>
              {section.label}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

/** Who wrote it, at the foot, where a reader has a reason to care. */
function AuthorCard({ author }: { author: Author }) {
  return (
    <Card padding={3}>
      <HStack gap={2} vAlign="start" wrap="wrap">
        <AuthorMark author={author} size="lg" />
        <VStack gap={0.5} className="author-card__body">
          <Text weight="medium">{author.url ? <Link href={author.url}>{author.name}</Link> : author.name}</Text>
          <Text size="sm" color="secondary">
            {author.role}
          </Text>
          <Text size="sm" color="secondary">
            {author.bio}
          </Text>
        </VStack>
      </HStack>
    </Card>
  );
}

/**
 * Older and newer neighbours, plus anything else worth reading.
 *
 * With one published article both lists are empty and the whole block renders
 * nothing, which is the correct behaviour — an empty "read next" rail is the
 * clearest possible signal that a blog has been abandoned.
 */
function ReadNext({ slug }: { slug: string }) {
  const { previous, next } = adjacentPosts(slug);
  const others = relatedPosts(slug);
  if (!previous && !next && others.length === 0) return null;

  return (
    <VStack gap={2}>
      <span className="eyebrow">Read next</span>
      <div className="read-next">
        {others.map((post) => (
          <RouterLink key={post.slug} to={postPath(post.slug)} className="read-next__card lift">
            <span className="read-next__dir">{post.kicker}</span>
            <span className="read-next__title">{post.headline}</span>
            <Text size="sm" color="secondary">
              {post.readingMinutes} min read
            </Text>
          </RouterLink>
        ))}
      </div>
      {previous || next ? (
        <HStack gap={2} wrap="wrap" hAlign="between">
          {previous ? <Link href={postPath(previous.slug)}>{`← ${previous.title}`}</Link> : <span />}
          {next ? <Link href={postPath(next.slug)}>{`${next.title} →`}</Link> : null}
        </HStack>
      ) : null}
    </VStack>
  );
}

export function BlogPostPage() {
  const { slug } = useParams();
  const post = findPost(slug);

  // Not `usePageEvent`: navigating from one post to another changes the param
  // without remounting this component, and an effect keyed on the event name
  // alone would count only the first article of a session.
  useEffect(() => {
    if (post) track('article_viewed', { slug: post.slug });
  }, [post]);

  const Body = slug === undefined ? undefined : ARTICLES[slug];
  if (!post || !Body) return <NotFoundPage />;

  const author = findAuthor(post.author);

  return (
    <div className="shell shell--narrow section-block">
      <article className="prose">
        <VStack gap={4}>
          <header className="post-hero">
            <RouterLink to="/blog" className="back-link">
              ← All posts
            </RouterLink>
            <div className="post-meta">
              <span className="kicker">{post.kicker}</span>
              <span>{post.keyword}</span>
            </div>
            <Heading level={1} className="post-hero__title">
              {post.headline}
            </Heading>
            <Byline author={author} post={post} />
          </header>

          <Contents post={post} />

          <Body />

          <Divider />

          {/* One CTA, one sentence, at the end — docs/marketing/content-seo.md. */}
          <div className="cta-band">
            <div className="cta-band__inner">
              <div>
                <div className="cta-band__title">Run it on your own series.</div>
                <div className="cta-band__text">
                  If you want to try this without hosting a model, VecTime Cloud&apos;s free tier is
                  300 forecasts a month and needs no card.
                </div>
              </div>
              <HStack gap={1.5} wrap="wrap">
                <a className="btn-light" href="/register">
                  Start free
                </a>
                <a className="btn-ghost" href="/docs">
                  Read the API docs
                </a>
              </HStack>
            </div>
          </div>

          {author ? <AuthorCard author={author} /> : null}

          <ReadNext slug={post.slug} />
        </VStack>
      </article>

      <JsonLd doc={articleSchema(post, author)} />
    </div>
  );
}
